// frontend/src/pages/Onboarding/CreateProfile.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Classes from "./Profile.module.css";
import { CarFrontIcon, LocationIcon } from "../../../../utils/Icons";
import LocationInfo from "./Comp/LocationInfo";
import VehicleInfo from "./Comp/VehicleInfo";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import backend from "../../../../api";
import { setUserData } from "../../../../redux-store/auth/actions";

const STORAGE_KEY = "fuelclubsOnboardingDraft";

const slideVariants = {
  initial: { x: 10, opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: -10, opacity: 0 },
};

export default function CreateProfile() {
  const { user } = useSelector((s) => s.auth);
  const dispatch = useDispatch();

  const navigateTo = useNavigate();
  const [activePage, setActivePage] = useState(1);
  const [profile, setProfile] = useState({
    // ▸ basics (new)
    firstName: "",
    lastName: "",
    phone: "",
    // ▸ location + vehicle (existing)
    location: {
      address: "",
      neighborhood: "",
      company: "",
      gateCode: "",
      parkingSpot: "",
      notes: "",
      city: "",
      state: "",
      postalCode: "",
      // country: "",
      placeId: "",
      lat: "",
      lng: "",
    },
    vehicle: { vehicles: [{ make: "", model: "", plate: "" }] },
  });

  /* ───────── load/save draft ───────── */
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return;
    try {
      const { step, profile: p } = JSON.parse(saved);
      if (step) setActivePage(step);
      if (p) setProfile((prev) => ({ ...prev, ...p }));
    } catch {/* ignore */ }
  }, []);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ step: activePage, profile }));
    });
    return () => cancelAnimationFrame(id);
  }, [activePage, profile]);

  const goNext = () => setActivePage((p) => Math.min(p + 1, 2));
  const goBack = () => setActivePage((p) => Math.max(p - 1, 1));
  const goStep = (id) => setActivePage(id);

  const updateSection = useCallback((section, partial) => {
    setProfile((prev) => ({ ...prev, [section]: { ...prev[section], ...partial } }));
  }, []);

  const updateBasics = useCallback((partial) => {
    setProfile((prev) => ({ ...prev, ...partial }));
  }, []);

  /* ─────────── PUT /user/:id helper ───────────
     Accepts a patch object that may contain:
     - firstName, lastName, phone   (root)
     - location, vehicle            (inside customerProfile)
  */
  const saveProfile = useCallback(
    async (patch) => {
      if (!user?._id) return null;

      const payload = {};
      // Conditionally include root fields so we don't overwrite with undefined
      if (typeof patch.firstName === "string") payload.firstName = patch.firstName;
      if (typeof patch.lastName === "string") payload.lastName = patch.lastName;
      if (typeof patch.phone === "string") payload.phone = patch.phone;

      // Merge customerProfile sub-doc
      if (patch.location || patch.vehicle) {
        payload.customerProfile = {
          ...(user.customerProfile || {}),
          ...(patch.location ? { location: patch.location } : {}),
          ...(patch.vehicle ? { vehicle: patch.vehicle } : {}),
        };
      }

      const { data } = await backend.put(`/api/user/${user._id}`, payload);
      await dispatch(setUserData({ ...user, ...data.data }));
      return data?.data;
    },
    [dispatch, user]
  );

  const handleFinish = async () => {
    try {
      // persist EVERYTHING before navigating
      await saveProfile({
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone,
        location: profile.location,
        vehicle: profile.vehicle,
      });
      localStorage.removeItem(STORAGE_KEY);
      navigateTo("/onboard/membership");
    } catch (e) {
      console.error(e);
      alert("Something went wrong while saving.");
    }
  };

  const steps = [
    { id: 1, label: "Location Info", icon: <LocationIcon height="16" fill="#fff" /> },
    { id: 2, label: "Vehicle Info", icon: <CarFrontIcon height="16" fill="#fff" /> },
  ];

  const stepComponent = {
    1: (
      <LocationInfo
        basics={{ firstName: profile.firstName, lastName: profile.lastName, phone: profile.phone }}
        data={profile.location}
        // save basics on change (cheap PATCH)
        updateBasics={async (d) => {
          updateBasics(d);
          try { await saveProfile(d); } catch { }
        }}
        // save location on change (cheap PATCH)
        update={(d) => {
          updateSection("location", d);
          // Persist merged snapshot to avoid clobbering fields
          const nextLocation = { ...profile.location, ...d };
          try { saveProfile({ location: nextLocation }); } catch { }
        }}
        onNext={goNext}
      />
    ),
    2: (
      <VehicleInfo
        data={profile.vehicle}
        update={async (d) => {
          updateSection("vehicle", d);
          try {
            await saveProfile({ vehicle: { ...(profile.vehicle || {}), ...d } });
          } catch { }
        }}
        onBack={goBack}
        onFinish={handleFinish}
      />
    ),
  }[activePage];

  const bodyRef = useRef(null);
  useEffect(() => { bodyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [activePage]);

  return (
    <div className={Classes.hero}>
      <div className={Classes.header}>
        <a href="/" >
          <img src="/assets/images/logo.webp" draggable={false} />
        </a>
      </div>

      <div className={Classes.body} ref={bodyRef}>
        <div className={Classes.bodyInner}>
          <motion.h3 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: "tween", duration: 0.4 }}>Create Your Profile</motion.h3>
          <motion.h5 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: "tween", duration: 0.4, delay: 0.05 }}>
            Don’t worry — you can edit this later in your settings
          </motion.h5>

          <motion.div className={Classes.stepper} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: "tween", duration: 0.4, delay: 0.1 }}>
            {steps.map((s, idx) => (
              <React.Fragment key={s.id}>
                <div role="button"
                  onClick={() => goStep(s.id)}
                  className={`${Classes.sideElement} ${activePage === s.id ? Classes.current : ""}`}>
                  <div className={Classes.flexAlign10}>
                    <div className={`${Classes.imageCon} ${activePage >= s.id ? Classes.active : ""}`}>{s.icon}</div>
                    <h5 className={activePage >= s.id ? Classes.activeText : undefined}>{s.label}</h5>
                  </div>
                </div>
                {idx !== steps.length - 1 && (<div className={`${Classes.hr} ${activePage > s.id ? Classes.coloredHr : ""}`} />)}
              </React.Fragment>
            ))}
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div key={activePage} variants={slideVariants} initial="initial" animate="animate" exit="exit" className={Classes.infoMain}>
              {stepComponent}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
